import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/shared/hooks";
import {
  parseDate,
  matchesDateRange,
  normalizeDateToStartOfDay,
  normalizeDateToEndOfDay,
} from "@/shared/utils";
import type { Locale, DateRange } from "@/shared/utils";
import { useGetSelfRequestsQuery } from "../api";
import {
  filterRequestsByView,
  isRevisionRequested,
  ACTIVE_STATUS_GROUPS,
  HISTORY_STATUS_GROUPS,
  type RequestView,
} from "../utils/selfRequestRules";
import type { SelfServiceRequest } from "../types";

export interface RequestsStats {
  total: number;
  totalActive: number;
  onRevision: number;
  totalCompleted: number;
}

interface StatusOption {
  label: string;
  value: string;
}

const STATS_DEFAULTS: RequestsStats = { total: 0, totalActive: 0, onRevision: 0, totalCompleted: 0 };

export function useRequestsListPage() {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = i18n.language as Locale;

  const { data: allRequests, isLoading, isError, refetch } = useGetSelfRequestsQuery();

  const [view, setView] = useState<RequestView>("active");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  function handleViewChange(newView: RequestView) {
    setView(newView);
    setStatusFilter(null);
  }

  const viewFilteredRequests = useMemo(function computeViewFiltered() {
    return filterRequestsByView(allRequests, view);
  }, [allRequests, view]);

  const stats = useMemo<RequestsStats>(function computeStats() {
    if (!allRequests) return STATS_DEFAULTS;
    const active = filterRequestsByView(allRequests, "active");
    const history = filterRequestsByView(allRequests, "history");
    let onRevision = 0;
    for (const r of allRequests) {
      if (isRevisionRequested(r)) onRevision++;
    }
    return {
      total: allRequests.length,
      totalActive: active?.length ?? 0,
      onRevision,
      totalCompleted: history?.length ?? 0,
    };
  }, [allRequests]);

  const filteredRequests = useMemo(function filterRequests() {
    if (!viewFilteredRequests) return [];

    const term = search.trim().toLowerCase();

    return viewFilteredRequests.filter(function matchRequest(r: SelfServiceRequest) {
      if (term) {
        const typeLabel = (r.type_label?.[locale] || r.type_label?.ru || r.type).toLowerCase();
        const statusLabel = (r.status_label?.[locale] || r.status_label?.ru || r.status).toLowerCase();
        const itemName = (r.details?.item_name || "").toLowerCase();
        const description = (r.details?.description || "").toLowerCase();
        const reason = (r.details?.reason || "").toLowerCase();
        const matchesSearch =
          typeLabel.includes(term) ||
          statusLabel.includes(term) ||
          itemName.includes(term) ||
          description.includes(term) ||
          reason.includes(term);
        if (!matchesSearch) return false;
      }

      if (dateRange.start || dateRange.end) {
        const createdAt = parseDate(r.created_at);
        const matchesCreatedAt = matchesDateRange(
          createdAt,
          dateRange,
          normalizeDateToStartOfDay,
          normalizeDateToEndOfDay,
        );

        let matchesRequestDateRange = false;
        if (r.date_range?.start_date || r.date_range?.end_date) {
          const rangeStart = parseDate(r.date_range?.start_date ?? null);
          const rangeEnd = parseDate(r.date_range?.end_date ?? null);
          matchesRequestDateRange =
            matchesDateRange(rangeStart, dateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay) ||
            matchesDateRange(rangeEnd, dateRange, normalizeDateToStartOfDay, normalizeDateToEndOfDay);
        }

        if (!matchesCreatedAt && !matchesRequestDateRange) return false;
      }

      if (statusFilter) {
        const groups = view === "active" ? ACTIVE_STATUS_GROUPS : HISTORY_STATUS_GROUPS;
        const group = groups.find((g) => g.labelKey === statusFilter);
        if (group && !group.codes.includes(r.status)) return false;
      }

      return true;
    });
  }, [viewFilteredRequests, search, dateRange, statusFilter, view, locale]);

  const pagination = usePagination(filteredRequests.length, 9, [
    search,
    statusFilter,
    dateRange,
    view,
  ]);

  const pageRequests = filteredRequests.slice(pagination.startIndex, pagination.endIndex);

  const statusOptions = useMemo<StatusOption[]>(function computeStatusOptions() {
    const groups = view === "active" ? ACTIVE_STATUS_GROUPS : HISTORY_STATUS_GROUPS;
    return groups.map((g) => ({ value: g.labelKey, label: t(g.labelKey) }));
  }, [view, t]);

  const hasActiveFilters = !!search.trim() || !!dateRange.start || !!dateRange.end || !!statusFilter;

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
  }

  return {
    data: allRequests,
    isLoading,
    isError,
    refetch,
    view,
    setView: handleViewChange,
    filteredRequests,
    pagination,
    pageRequests,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    hasActiveFilters,
    handleResetFilters,
    stats,
    locale,
  };
}
